/**
 * ProShip Direct v2 — PACE engine settings.
 *
 * The knobs are PACE v2's real business knobs, mirrored from the admin catalogue — see the
 * provenance header on data/paceKnobs.js. The earlier version of this page invented five
 * (cost / speed / firstAttempt / rto / pickupReliability) that do not exist in the engine, so a
 * merchant could tune it for an afternoon and change nothing.
 *
 * Three things about the real contract drive the whole layout:
 *
 *   SCOPE. Values live at `global` or `merchant#<id>`, and merchant wins. So no knob is ever
 *   missing — anything the merchant has not set is running on ProShip's default, and every row
 *   says which. Same fallback story as serviceability and zones.
 *
 *   AUDIT. The real PUT refuses a change without a reason and appends to a per-knob history. So
 *   edits are held as pending until a reason is given, the count of pending changes is always
 *   visible, and the history is on the page rather than hidden.
 *
 *   PROFILE. `cheapest` and `fastest` ignore the blend and the performance weights entirely;
 *   `balanced` uses the blend; only `weighted` reads all four weights. Tuning knobs the chosen
 *   profile ignores is the easiest mistake here, so groups the profile does not use are dimmed and
 *   labelled rather than hidden — hiding them would make the setting look broken.
 *
 * WHAT A MERCHANT SEES is ten knobs in four groups, all of them answerable in plain business terms:
 * cheaper or faster, what each performance measure is worth, what a failure costs in rupees, and
 * one hard limit. The other nine — the engine's own Advanced tier: lambda normalisers, recency
 * multipliers, support thresholds, the logistics cap — are collapsed into a single section that
 * says plainly they are best left alone. They are not hidden, because a merchant told to change one
 * by support needs to find it; they are just not presented as ordinary settings, because a merchant
 * cannot reason about them and the defaults are tuned on ProShip-wide traffic rather than one
 * merchant's lanes.
 *
 * And the way out of all of it is one button: ProShip recommended clears every override and puts
 * the profile back. It is not a second set of numbers — the recommended values ARE the catalogue
 * defaults, so the recommendation cannot drift from what ProShip actually recommends.
 *
 * One knob set, not one per courier pool. Ashvini's version held separate sets for own and ProShip
 * couriers; with modes gone there is nothing to hold a second set against. The counter-argument is
 * real and worth recording: on your own contract you carry the SLA and the reattempt cost, so those
 * arguably deserve different weights than on a ProShip rate that already prices the service. If
 * that turns out to matter, the answer is a per-courier override, not a return to modes.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Grid, Typography, Slider, Button, Alert, TextField, Chip, Tooltip, IconButton, Divider,
  Collapse, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import PageHero from '../../../common/PageHero';
import { usePaceKnobs, usePricingModel, useCourierPool } from '../data/hooks';
import {
  BASIC_GROUPS, ADVANCED_KNOBS, ADVANCED_SECTION, SCORECARD_WEIGHT_KEYS, PACE_PROFILES,
  RECOMMENDED, isOnRecommended, findProfile, findKnob, formatKnob, knobError, knobsInGroup,
  sliderCeiling,
} from '../data/paceKnobs';
import { pageContainerSx, sectionCardSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { tHeadSx, tCellSx, statCardSx, statLabelSx, statValueSx, statSubSx } from '../components/stats';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, WHITE, SUCCESS, WARNING, ERROR, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { formatDate } from '../../../helpers/UtilityHelper';
import { MONO } from '../components/directCellStyles';

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.6 };
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };

/** Which knob groups the chosen profile actually reads. */
const groupIsLive = (profile, group) => findProfile(profile).uses.includes(group);

/** One profile, picked by clicking it. */
function ProfileCard({ profile, selected, disabled, onSelect }) {
  return (
    <Box
      role='button'
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect(profile.value)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !disabled && onSelect(profile.value)}
      sx={{
        height: '100%',
        p: 1.25,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: `1px solid ${selected ? PRIMARY_BLUE : BORDER}`,
        bgcolor: selected ? `${PRIMARY_BLUE}0a` : WHITE,
        borderRadius: RADIUS.md,
        '&:hover': disabled ? undefined : { borderColor: PRIMARY_BLUE },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, flexWrap: 'wrap' }}>
        {selected && <CheckCircleIcon sx={{ fontSize: 15, color: PRIMARY_BLUE }} />}
        <Typography sx={strongSx}>{profile.label}</Typography>
        {profile.recommended && (
          <Chip
            size='small'
            label='recommended'
            sx={{
              height: 17,
              fontFamily: FONT,
              fontSize: SIZE['4xs'],
              fontWeight: WEIGHT.semibold,
              bgcolor: `${SUCCESS}14`,
              color: SUCCESS,
            }}
          />
        )}
      </Box>
      <Typography sx={smallSx}>{profile.blurb}</Typography>
    </Box>
  );
}

/**
 * One knob row.
 *
 * Sliders for the bounded ones — a 0-to-1 blend is a slider in every product that has ever shipped
 * one — and a number field for the unbounded rupee amounts, where dragging to ₹1,200 would be
 * absurd. Both show the effective value, whether it is the merchant's or ProShip's, and a way back.
 */
function KnobRow({ knob, value, pending, overridden, disabled, onEdit, onReset, onHistory }) {
  const shown = pending !== undefined ? pending : value;
  const error = pending !== undefined ? knobError(knob, pending) : null;
  const top = sliderCeiling(knob);
  const bounded = knob.min !== null && knob.min !== undefined && top !== null && top !== undefined;

  return (
    <Box sx={{ py: 1.25, borderBottom: `1px solid ${BORDER}`, '&:last-of-type': { borderBottom: 'none' } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: GAP.sm, mb: bounded ? 0.5 : 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
            <Typography sx={strongSx}>{knob.label}</Typography>
            {pending !== undefined ? (
              <Chip
                size='small'
                label='unsaved'
                sx={{
                  height: 17,
                  fontFamily: FONT,
                  fontSize: SIZE['4xs'],
                  fontWeight: WEIGHT.semibold,
                  bgcolor: `${WARNING}1f`,
                  color: WARNING,
                }}
              />
            ) : (
              <Tooltip
                title={
                  overridden
                    ? 'Your own value, set for this merchant'
                    : 'Running on the ProShip default. Nothing is unset — this is the working value.'
                }
              >
                <Chip
                  size='small'
                  label={overridden ? 'Direct' : 'ProShip default'}
                  sx={{
                    height: 17,
                    fontFamily: FONT,
                    fontSize: SIZE['4xs'],
                    fontWeight: overridden ? WEIGHT.semibold : WEIGHT.regular,
                    bgcolor: overridden ? `${SUCCESS}14` : LIGHT_BG,
                    color: overridden ? SUCCESS : SUBTLE,
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <Typography sx={smallSx}>{knob.description}</Typography>
        </Box>

        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography sx={{ fontFamily: MONO, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY }}>
            {formatKnob(knob, shown)}
          </Typography>
          <Typography sx={{ ...smallSx, fontSize: 10 }}>default {formatKnob(knob, knob.default)}</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexShrink: 0 }}>
          {overridden && !disabled && (
            <Tooltip title='Back to the ProShip default'>
              <IconButton size='small' onClick={() => onReset(knob.key)} aria-label={`Reset ${knob.label}`}>
                <RestartAltOutlinedIcon sx={{ fontSize: 16, color: SUBTLE }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Change history'>
            <IconButton size='small' onClick={() => onHistory(knob.key)} aria-label={`History for ${knob.label}`}>
              <HistoryOutlinedIcon sx={{ fontSize: 16, color: SUBTLE }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {bounded ? (
        <Box sx={{ px: 0.5 }}>
          <Slider
            size='small'
            value={Number(shown) || 0}
            min={knob.min}
            max={top}
            step={knob.step || 0.05}
            disabled={disabled}
            onChange={(e, next) => onEdit(knob.key, next)}
            sx={{ '& .MuiSlider-thumb': { width: 13, height: 13 } }}
          />
          {knob.ends && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.75 }}>
              <Typography sx={{ ...smallSx, fontSize: 10 }}>{knob.ends[0]}</Typography>
              <Typography sx={{ ...smallSx, fontSize: 10 }}>{knob.ends[1]}</Typography>
            </Box>
          )}
        </Box>
      ) : (
        <TextField
          size='small'
          type='number'
          value={shown ?? ''}
          disabled={disabled}
          onChange={(e) => onEdit(knob.key, e.target.value)}
          error={Boolean(error)}
          helperText={error}
          FormHelperTextProps={{ sx: { fontFamily: FONT, fontSize: 10, ml: 0 } }}
          inputProps={{ min: knob.min ?? undefined, max: knob.max ?? undefined, step: knob.step || 'any' }}
          sx={{
            width: 180,
            '& .MuiInputBase-input': { fontFamily: MONO, fontSize: 12 },
          }}
        />
      )}
    </Box>
  );
}

/** A knob group, with whether the chosen profile reads it. */
function KnobGroup({ group, profile, children }) {
  const live = groupIsLive(profile, group.key);

  return (
    <Box sx={{ ...sectionCardSx, opacity: live ? 1 : 0.62 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: GAP.sm, mb: 1.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>
              {group.title}
            </Typography>
            {!live && (
              <Tooltip title={`The ${findProfile(profile).label} profile does not read these. Change the profile above to use them.`}>
                <Chip
                  size='small'
                  icon={<LockOutlinedIcon sx={{ fontSize: 12 }} />}
                  label={`not used by ${findProfile(profile).label}`}
                  sx={{ height: 18, fontFamily: FONT, fontSize: SIZE['4xs'], bgcolor: LIGHT_BG, color: SUBTLE }}
                />
              </Tooltip>
            )}
          </Box>
          <Typography sx={smallSx}>{group.blurb}</Typography>
        </Box>
      </Box>

      {children}
    </Box>
  );
}

/**
 * ProShip's recommendation, and whether the merchant is on it.
 *
 * Leads the page because it is the answer for most merchants: a tuned starting point they never
 * have to touch. Applying it clears every override rather than writing values, so it can never
 * disagree with the defaults in the catalogue.
 */
function RecommendedCard({ onApply, active, overrideCount, profileLabel, disabled }) {
  return (
    <Box
      sx={{
        ...sectionCardSx,
        borderColor: active ? `${SUCCESS}55` : `${PRIMARY_BLUE}55`,
        bgcolor: active ? `${SUCCESS}0a` : `${PRIMARY_BLUE}0a`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: GAP.md, flexWrap: 'wrap' }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: RADIUS.md,
            bgcolor: WHITE,
            color: active ? SUCCESS : PRIMARY_BLUE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {active ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>
            {active ? 'You are on the ProShip recommended settings' : RECOMMENDED.title}
          </Typography>
          <Typography sx={smallSx}>{RECOMMENDED.blurb}</Typography>
          {!active && (
            <Typography sx={{ ...smallSx, mt: 0.5, color: NAVY }}>
              You are on {profileLabel}
              {overrideCount > 0 ? ` with ${overrideCount} setting${overrideCount === 1 ? '' : 's'} of your own` : ''}.
            </Typography>
          )}
        </Box>
        {!active && (
          <Button
            variant='contained'
            size='small'
            sx={primaryBtnSx}
            disabled={disabled}
            onClick={onApply}
          >
            Use recommended
          </Button>
        )}
      </Box>
    </Box>
  );
}

function PaceEngine() {
  const { profile, overrides, history, valueOf, isOverridden, commit, setProfile, resetAllKnobs } = usePaceKnobs();
  const { isBlended } = usePricingModel();
  const { pool } = useCourierPool();

  const [edits, setEdits] = useState({});
  const [reason, setReason] = useState('');
  const [historyKey, setHistoryKey] = useState(null);
  const [saved, setSaved] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const readOnly = isBlended;

  const pendingKeys = Object.keys(edits);
  const invalid = pendingKeys.filter((key) => {
    if (edits[key] === null) return false; // a reset is always valid
    return Boolean(knobError(findKnob(key), edits[key]));
  });

  const onEdit = (key, value) => {
    setSaved(null);
    setEdits((prev) => {
      const knob = findKnob(key);
      const current = valueOf(key);
      // Dragging a slider back to exactly where it was is not a change.
      if (Number(value) === Number(current)) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: knob && !knob.step ? value : Number(value) };
    });
  };

  const onReset = (key) => {
    setSaved(null);
    setEdits((prev) => ({ ...prev, [key]: null }));
  };

  const save = () => {
    if (!reason.trim() || !pendingKeys.length || invalid.length) return;
    commit(edits, reason.trim());
    setSaved(pendingKeys.length);
    setEdits({});
    setReason('');
  };

  /**
   * The four performance weights as percentages of their own total.
   *
   * PACE normalises them, so the raw numbers mean nothing on their own — 0.4/0.2/0.2/0.2 and
   * 40/20/20/20 are the same engine. The percentage is the only reading a merchant can act on.
   */
  const weightShares = useMemo(() => {
    const values = SCORECARD_WEIGHT_KEYS.map((key) => {
      const pending = edits[key];
      const raw = pending === null ? findKnob(key).default : pending !== undefined ? pending : valueOf(key);
      return { key, label: findKnob(key).label, value: Math.max(0, Number(raw) || 0) };
    });
    const total = values.reduce((sum, v) => sum + v.value, 0);
    return values.map((v) => ({ ...v, share: total ? Math.round((v.value / total) * 100) : 0 }));
  }, [edits, valueOf]);

  const knobHistory = historyKey ? history.filter((h) => h.key === historyKey) : [];
  const overrideCount = Object.keys(overrides).length;

  const rowsFor = (groupKey, advanced) =>
    knobsInGroup(groupKey, advanced).map((knob) => (
      <KnobRow
        key={knob.key}
        knob={knob}
        value={valueOf(knob.key)}
        pending={edits[knob.key] === null ? knob.default : edits[knob.key]}
        overridden={isOverridden(knob.key)}
        disabled={readOnly}
        onEdit={onEdit}
        onReset={onReset}
        onHistory={setHistoryKey}
      />
    ));

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='What PACE optimises for when it picks a courier per order'>
        PACE Engine
      </PageTitle>

      <PageHero
        title='PACE allocation'
        subtitle={`${findProfile(profile).label} · ${pool.length} couriers in the pool · ${overrideCount === 0 ? 'all settings on ProShip defaults' : `${overrideCount} setting${overrideCount === 1 ? '' : 's'} of your own`}`}
      />

      {!readOnly && (
        <Box sx={{ mb: 2 }}>
          <RecommendedCard
            active={isOnRecommended(profile, overrides)}
            overrideCount={Object.keys(overrides).length}
            profileLabel={findProfile(profile).label}
            disabled={readOnly}
            onApply={() => {
              setEdits({});
              setReason('');
              setSaved(null);
              if (profile !== RECOMMENDED.profile) setProfile(RECOMMENDED.profile);
              resetAllKnobs('Applied the ProShip recommended settings');
            }}
          />
        </Box>
      )}

      {readOnly && (
        <Alert severity='info' sx={{ mb: 2, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
          You are on blended pricing, so courier choice is ProShip&rsquo;s to make and these settings
          are read-only. Everything below is the configuration in force — move to courier-wise
          pricing to tune it yourself.
        </Alert>
      )}

      <Grid container spacing={2} alignItems='flex-start'>
        <Grid item xs={12} lg={8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
            {/* ── Profile ── */}
            <Box sx={sectionCardSx}>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>
                Allocation profile
              </Typography>
              <Typography sx={{ ...smallSx, mb: 1.25 }}>
                Decides which of the settings below PACE reads. The others stay saved but do nothing.
              </Typography>
              <Grid container spacing={1.25}>
                {PACE_PROFILES.map((p) => (
                  <Grid item xs={6} md={3} key={p.value}>
                    <ProfileCard
                      profile={p}
                      selected={p.value === profile}
                      disabled={readOnly}
                      onSelect={setProfile}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* ── The ten a merchant can answer, in the order they should meet them ── */}
            {BASIC_GROUPS.map((group) => {
              const plain = rowsFor(group.key, false);
              if (!plain.length) return null;

              return (
                <KnobGroup key={group.key} group={group} profile={profile}>
                  {/* The weights only make sense read as shares, so the shares lead. */}
                  {group.key === 'scorecard' && (
                    <Box sx={{ mb: 1.5 }}>
                      <Grid container spacing={1}>
                        {weightShares.map((w) => (
                          <Grid item xs={6} md={3} key={w.key}>
                            <Box sx={statCardSx}>
                              <Typography sx={statLabelSx}>{w.label}</Typography>
                              <Typography sx={statValueSx}>{w.share}%</Typography>
                              <Typography sx={statSubSx}>of the score</Typography>
                              <LinearProgress
                                variant='determinate'
                                value={w.share}
                                sx={{
                                  mt: 0.5,
                                  height: 4,
                                  borderRadius: 3,
                                  bgcolor: LIGHT_BG,
                                  '& .MuiLinearProgress-bar': { bgcolor: PRIMARY_BLUE },
                                }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                      {weightShares.every((w) => w.share === 0) && (
                        <Alert severity='warning' sx={{ mt: 1, fontFamily: FONT, fontSize: SIZE['3xs'] }}>
                          Every weight is zero, so nothing is scored. PACE would fall back to
                          whatever the hard limits leave standing.
                        </Alert>
                      )}
                    </Box>
                  )}
                  {plain}
                </KnobGroup>
              );
            })}

            {/* ── The nine that are nobody's business but support's ── */}
            <Box sx={sectionCardSx}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: GAP.sm }}>
                <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: WARNING, mt: '2px' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>
                    {ADVANCED_SECTION.title}
                  </Typography>
                  <Typography sx={smallSx}>{ADVANCED_SECTION.blurb}</Typography>
                </Box>
                <Button
                  size='small'
                  sx={secondaryBtnSx}
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 16,
                        transform: showAdvanced ? 'rotate(180deg)' : 'none',
                        transition: 'transform 150ms',
                      }}
                    />
                  }
                  onClick={() => setShowAdvanced((v) => !v)}
                >
                  {showAdvanced ? 'Hide' : `Show ${ADVANCED_KNOBS.length}`}
                </Button>
              </Box>

              <Collapse in={showAdvanced}>
                <Box sx={{ mt: 1.5 }}>
                  {ADVANCED_KNOBS.map((knob) => (
                    <KnobRow
                      key={knob.key}
                      knob={knob}
                      value={valueOf(knob.key)}
                      pending={edits[knob.key] === null ? knob.default : edits[knob.key]}
                      overridden={isOverridden(knob.key)}
                      disabled={readOnly}
                      onEdit={onEdit}
                      onReset={onReset}
                      onHistory={setHistoryKey}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Grid>

        {/* ── Save, and the audit trail ── */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ position: { lg: 'sticky' }, top: 12, display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
            <Box sx={sectionCardSx}>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY, mb: 0.25 }}>
                {pendingKeys.length ? `${pendingKeys.length} unsaved change${pendingKeys.length === 1 ? '' : 's'}` : 'No unsaved changes'}
              </Typography>
              <Typography sx={{ ...smallSx, mb: 1.25 }}>
                Every change to allocation is recorded against your account with a reason. That is
                the engine&rsquo;s requirement, not a form field we added.
              </Typography>

              {pendingKeys.length > 0 && (
                <Box sx={{ mb: 1.25, p: 1, bgcolor: LIGHT_BG, borderRadius: RADIUS.md }}>
                  {pendingKeys.map((key) => {
                    const knob = findKnob(key);
                    const to = edits[key] === null ? knob.default : edits[key];
                    return (
                      <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.2 }}>
                        <Typography sx={smallSx}>{knob.label}</Typography>
                        <Typography sx={{ ...smallSx, color: NAVY, fontFamily: MONO, whiteSpace: 'nowrap' }}>
                          {formatKnob(knob, valueOf(key))} → {formatKnob(knob, to)}
                          {edits[key] === null ? ' (default)' : ''}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              <TextField
                size='small'
                fullWidth
                label='Reason for this change'
                value={reason}
                disabled={readOnly || !pendingKeys.length}
                onChange={(e) => setReason(e.target.value)}
                placeholder='RTO on COD lanes up 3% this month'
                sx={{
                  mb: 1.25,
                  '& .MuiInputBase-input': { fontFamily: FONT, fontSize: 12 },
                  '& .MuiInputLabel-root': { fontSize: 10 },
                }}
              />

              <Box sx={{ display: 'flex', gap: GAP.sm, flexWrap: 'wrap' }}>
                <Button
                  variant='contained'
                  size='small'
                  sx={primaryBtnSx}
                  disabled={readOnly || !pendingKeys.length || !reason.trim() || invalid.length > 0}
                  onClick={save}
                >
                  Save changes
                </Button>
                {pendingKeys.length > 0 && (
                  <Button
                    size='small'
                    sx={secondaryBtnSx}
                    onClick={() => {
                      setEdits({});
                      setReason('');
                    }}
                  >
                    Discard
                  </Button>
                )}
              </Box>

              {invalid.length > 0 && (
                <Typography sx={{ ...smallSx, mt: 1, color: ERROR }}>
                  {invalid.length} value{invalid.length === 1 ? ' is' : 's are'} outside the range the
                  engine accepts.
                </Typography>
              )}
              {saved && (
                <Alert severity='success' sx={{ mt: 1.25, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                  {saved} change{saved === 1 ? '' : 's'} saved. PACE picks them up on the next order.
                </Alert>
              )}

              {overrideCount > 0 && !readOnly && (
                <>
                  <Divider sx={{ my: 1.5, borderColor: BORDER }} />
                  <Button
                    size='small'
                    sx={secondaryBtnSx}
                    startIcon={<RestartAltOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => resetAllKnobs('Reset every knob to the ProShip default')}
                  >
                    Reset all {overrideCount} to ProShip defaults
                  </Button>
                </>
              )}
            </Box>

            {/* ── History ── */}
            <Box sx={sectionCardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, mb: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>
                  {historyKey ? `History · ${findKnob(historyKey)?.label || historyKey}` : 'Recent changes'}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {historyKey && (
                  <Button size='small' sx={secondaryBtnSx} onClick={() => setHistoryKey(null)}>
                    Show all
                  </Button>
                )}
              </Box>

              {(historyKey ? knobHistory : history).length === 0 ? (
                <Typography sx={bodySx}>
                  {historyKey
                    ? 'This knob has never been changed — it has been on the ProShip default throughout.'
                    : 'Nothing changed yet. Every edit will appear here with who made it and why.'}
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...tHeadSx, minWidth: 110 }}>Knob</TableCell>
                        <TableCell sx={{ ...tHeadSx, minWidth: 90 }}>Change</TableCell>
                        <TableCell sx={{ ...tHeadSx, minWidth: 130 }}>Why, and who</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(historyKey ? knobHistory : history).slice(0, 12).map((row, i) => {
                        const knob = findKnob(row.key);
                        return (
                          <TableRow key={`${row.key}-${row.at}-${i}`}>
                            <TableCell sx={{ ...tCellSx, whiteSpace: 'normal' }}>{row.label}</TableCell>
                            <TableCell sx={{ ...tCellSx, fontFamily: MONO, whiteSpace: 'nowrap' }}>
                              {knob ? formatKnob(knob, row.from) : String(row.from)} →{' '}
                              {knob ? formatKnob(knob, row.to) : String(row.to)}
                            </TableCell>
                            <TableCell sx={{ ...tCellSx, whiteSpace: 'normal' }}>
                              {row.reason}
                              <Typography sx={{ ...smallSx, fontSize: 10 }}>
                                {row.by} · {formatDate(row.at, 'dd MMM, hh:mm a')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default withLayout(PaceEngine);
